import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import type { CurrentUser } from "../auth/useCurrentUser";
import { TIER_NAMES, TierLockModal } from "../components/TierLockModal";
import { BlueprintBackdrop } from "../components/BlueprintBackdrop";
import { UpdateBanner } from "../components/UpdateBanner";
import { MobileContext } from "../context/MobileContext";
import { useGridSnap } from "../hooks/useGridSnap";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { useVersionCheck } from "../hooks/useVersionCheck";
import { theme } from "../theme";
import { AppSidebar } from "./AppSidebar";
import { AppTopBar } from "./AppTopBar";
import { AreaTabs } from "./AreaTabs";
import { finaliseNavModel, NavModelContext } from "./areas";
import type { NavModelValue } from "./areas";
import type { TopBarBell } from "./AppTopBar";
import { CommandPalette } from "./CommandPalette";
import { useRouterLinkAdapter } from "./LinkAdapter";
import type { LinkAdapter } from "./LinkAdapter";
import { NavCountsContext } from "./NavCountsContext";
import type { NavCounts } from "./NavCountsContext";
import { useNavCounts } from "./useNavCounts";
import { useSidebarState } from "./useSidebarState";
import type { NavItem, NavModel, PaletteEntry, WorkspaceSwitch } from "./types";
import { visibleNavItems } from "./types";

// ── App shell ─────────────────────────────────────────────────────────────
// Sidebar + top bar + content column. Owns the mobile breakpoint (MobileContext),
// the single useVersionCheck() call, the NavCounts provider, the command
// palette (Ctrl/Cmd+K), the "[" collapse shortcut and the TierLockModal for
// locked rows.

export interface AppShellProps {
  /**
   * The nav model, or a builder that receives the polled counts (the client
   * shell needs them for framework chips, Public API gating and the setup
   * checklist before the model exists).
   */
  model: NavModel | ((counts: NavCounts | null) => NavModel);
  adapter?: LinkAdapter;
  user: CurrentUser;
  topBar: {
    breadcrumb?: ReactNode;
    bell?: TopBarBell;
    /** The statutory deadline clock, on the shells that carry client counts. */
    deadlineClock?: boolean;
  };
  userMenu: {
    switches: WorkspaceSwitch[];
    showExperienceMode: boolean;
    profileTo?: string;
    versionTo: string;
  };
  paletteExtras?: () => Promise<PaletteEntry[]>;
  /**
   * Shell-relative path of the More tools page. Set by the client shells: the
   * pages the sidebar leaves out (not relevant yet, or a higher tier) are listed
   * there behind one row (nav/areas.ts).
   */
  moreToolsTo?: string;
  /** Section the More tools row joins. */
  moreToolsSection?: string;
  /** The organisation's current service tier, for the lock modal copy. */
  tierLevel?: number;
  /** Rendered above the sidebar and content (the own-organisation strip). */
  banner?: ReactNode;
  /**
   * Identifies what the counts belong to; changing it re-fetches immediately
   * (the read-through passes the client's id).
   */
  countsKey?: string;
  children: ReactNode;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return target.isContentEditable;
}

export function AppShell({
  model: modelInput, adapter: adapterProp, user, topBar, userMenu, paletteExtras, tierLevel = 1, banner, countsKey, children,
  moreToolsTo, moreToolsSection,
}: AppShellProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const location = useLocation();
  const defaultAdapter = useRouterLinkAdapter();
  const adapter = adapterProp ?? defaultAdapter;

  const { updateAvailable, justUpdated, currentVersion, releases, dismissJustUpdated } = useVersionCheck();

  const shellKey = typeof modelInput === "function" ? undefined : modelInput.shellKey;
  const poller = useNavCounts(countsKey ?? shellKey ?? "shell");
  const { counts, refresh, refreshIfStale } = poller;
  const countsValue = useMemo(() => ({ counts, refresh }), [counts, refresh]);

  const rawModel = typeof modelInput === "function" ? modelInput(counts) : modelInput;
  const model = useMemo(
    () => finaliseNavModel(rawModel, { moreToolsTo, moreToolsSection }),
    [rawModel, moreToolsTo, moreToolsSection],
  );

  const sidebar = useSidebarState(model.shellKey);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const paletteOpenRef = useRef(false);
  paletteOpenRef.current = paletteOpen;
  const [locked, setLocked] = useState<NavItem | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // The scrolling page: its sheets are laid on the grid the backdrop draws behind it.
  const mainRef = useRef<HTMLElement>(null);
  useGridSnap(mainRef);
  const paletteReturnFocus = useRef<HTMLElement | null>(null);

  // Badges go stale the moment the user completes something; a navigation is
  // a cheap signal to look again (throttled inside the poller).
  useEffect(() => { refreshIfStale(); }, [location.pathname, refreshIfStale]);

  // Leaving the mobile breakpoint discards the drawer state.
  useEffect(() => { if (!isMobile) setMobileOpen(false); }, [isMobile]);

  // While the drawer is open the page behind it is inert: Tab cannot reach it
  // and assistive technology does not read it. `inert` is not in the React 18
  // JSX typings, so it is set on the element directly. Focus returns to the
  // hamburger here, once the column is no longer inert (an inert element
  // refuses focus), rather than in closeMobile itself.
  const drawerOpen = isMobile && mobileOpen;
  const drawerWasOpen = useRef(false);
  useEffect(() => {
    const el = contentRef.current as (HTMLDivElement & { inert?: boolean }) | null;
    if (el) el.inert = drawerOpen;
    if (!drawerOpen && drawerWasOpen.current) menuButtonRef.current?.focus();
    drawerWasOpen.current = drawerOpen;
    return () => { if (el) el.inert = false; };
  }, [drawerOpen]);

  const closeMobile = useCallback(() => { setMobileOpen(false); }, []);

  const closePalette = useCallback(() => {
    setPaletteOpen(false);
    const el = paletteReturnFocus.current;
    paletteReturnFocus.current = null;
    if (el && document.contains(el)) el.focus();
  }, []);

  // Keyboard: Ctrl/Cmd+K opens the palette; "[" toggles the rail when focus is
  // not in a text field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (paletteOpenRef.current) {
          closePalette();
        } else {
          paletteReturnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
          setPaletteOpen(true);
        }
        return;
      }
      if (e.key === "[" && !e.ctrlKey && !e.metaKey && !e.altKey && !isEditableTarget(e.target)) {
        if (isMobile) return;
        e.preventDefault();
        sidebar.toggleCollapsed();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobile, sidebar.toggleCollapsed, closePalette]);

  const paletteEntries = useMemo<PaletteEntry[]>(() => {
    return visibleNavItems(model)
      .filter(({ item }) => !item.children && !item.tabs && !item.lock && (item.to || item.onSelect))
      .map(({ item, section, group }) => ({
        id: `nav:${item.id}`,
        label: item.label,
        hint: [section.label, group?.label].filter(Boolean).join(" / ") || undefined,
        keywords: item.keywords,
        to: item.to ? adapter.href(item.to) : undefined,
        onSelect: item.onSelect,
      }));
  }, [model, adapter]);

  const navModelValue = useMemo<NavModelValue>(() => ({ model, adapter, onLocked: setLocked }), [model, adapter]);

  const lockedTierName = locked?.lock ? (TIER_NAMES[locked.lock.minTier] ?? `tier ${locked.lock.minTier}`) : "";

  return (
    <MobileContext.Provider value={isMobile}>
      <NavCountsContext.Provider value={countsValue}><NavModelContext.Provider value={navModelValue}>
        <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: theme.color.bg, fontFamily: theme.font, color: theme.color.text, overflow: "hidden" }}>
          {banner}
          <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
            <AppSidebar
              model={model}
              adapter={adapter}
              counts={counts}
              collapsed={sidebar.collapsed}
              onToggleCollapsed={sidebar.toggleCollapsed}
              isGroupOpen={sidebar.isGroupOpen}
              onToggleGroup={sidebar.toggleGroup}
              isMobile={isMobile}
              mobileOpen={mobileOpen}
              onMobileClose={closeMobile}
              onLocked={setLocked}
              version={currentVersion}
              versionTo={userMenu.versionTo}
              showExperienceMode={userMenu.showExperienceMode}
            />
            <div ref={contentRef} aria-hidden={drawerOpen || undefined} style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
              <AppTopBar
                ref={menuButtonRef}
                user={user}
                isMobile={isMobile}
                mobileOpen={mobileOpen}
                onMenuClick={() => setMobileOpen(true)}
                breadcrumb={topBar.breadcrumb}
                bell={topBar.bell ?? null}
                deadlineClock={topBar.deadlineClock ?? false}
                userMenu={{
                  switches: userMenu.switches,
                  showExperienceMode: userMenu.showExperienceMode,
                  profileTo: userMenu.profileTo,
                  versionTo: userMenu.versionTo,
                  version: currentVersion,
                }}
              />
              <AreaTabs model={model} adapter={adapter} counts={counts} isMobile={isMobile} />
              <div style={{ flex: 1, position: "relative", display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0 }}>
                <BlueprintBackdrop surface="app" scrollRef={mainRef} />
                <main ref={mainRef} className="paper-grid paper-grid--live" style={{ flex: 1, overflowY: "auto", minWidth: 0, position: "relative", zIndex: 1 }}>
                  {children}
                </main>
              </div>
            </div>
          </div>
        </div>

        <UpdateBanner
          updateAvailable={updateAvailable}
          justUpdated={justUpdated}
          releases={releases}
          onDismissJustUpdated={dismissJustUpdated}
        />

        <CommandPalette open={paletteOpen} onClose={closePalette} entries={paletteEntries} extras={paletteExtras} />

        {locked?.lock && (
          <TierLockModal
            featureName={locked.lock.feature}
            requiredTierName={lockedTierName}
            requiredTierLevel={locked.lock.minTier}
            currentTierLevel={tierLevel}
            onClose={() => setLocked(null)}
            // Nothing else closes the drawer on a route change, so it goes here.
            onRequestUpgrade={() => { setLocked(null); closeMobile(); adapter.navigate("/my-services"); }}
          />
        )}
      </NavModelContext.Provider></NavCountsContext.Provider>
    </MobileContext.Provider>
  );
}
